import React from "react";
import { Link } from "react-router-dom";
import { Crown, Calendar, Gift, RefreshCw, User, Sparkles, X, Plus, Bell, BellOff, CheckCircle2 } from "lucide-react";

const Card = ({ children, tone = "base", testid, className = "" }) => {
    const tones = {
        base: "border-[var(--pd-border)] bg-[var(--pd-surface)]",
        blue: "border-[var(--pd-accent-border)] bg-[var(--pd-accent-bg)]",
        purple: "border-[var(--pd-purple-border)] bg-[var(--pd-purple-bg)]",
    };
    return (
        <div data-testid={testid} className={`relative overflow-hidden rounded-2xl border p-5 md:p-6 ${tones[tone]} ${className}`}>
            {children}
        </div>
    );
};

const Eyebrow = ({ icon: Icon, children, color = "text-[var(--pd-accent)]", testid }) => (
    <div data-testid={testid} className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${color}`}>
        <Icon size={14} /> {children}
    </div>
);

const ghostBtn = "inline-flex items-center gap-2 rounded-xl border border-[var(--pd-accent-border)] bg-[var(--pd-surface)] px-4 py-2 text-xs font-medium text-[var(--pd-accent)] hover:bg-[var(--pd-accent-bg)] transition-colors";
const solidBtn = "inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-50";

function PlanCard({ sub, user, busy, onCancel }) {
    const promo = sub.promo || {};
    const perPlan = promo.per_plan_months || {};
    const maxMonths = Object.values(perPlan).reduce((m, v) => Math.max(m, Number(v) || 0), 0);
    const hasAnyPromo = promo.active && maxMonths > 0;
    const hasActivePlan = sub.plan_active && sub.plan_id;
    const isAddon = sub.plan_id === "analytics_pro";
    const freePeriod = sub.free_period;
    const paymentRequired = !!sub.payment_required;
    const isAgency = user?.role === "agenzia" || user?.is_admin;

    return (
        <Card testid="subscription-card">
            {sub.is_promo && (
                <div className="absolute right-0 top-0 rounded-bl-xl bg-[var(--pd-tag-amber-bg)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--pd-tag-amber)]">Promo lancio</div>
            )}
            <Eyebrow icon={Crown}>{paymentRequired ? "Pagamento richiesto" : hasActivePlan ? "Piano attivo" : "Nessun piano"}</Eyebrow>
            <div data-testid="sub-plan-name" className="mt-2 text-2xl font-semibold text-[var(--pd-text)]">{sub.plan_name || "Attiva un piano"}</div>
            {!hasActivePlan && hasAnyPromo && !user?.promo_used && user?.role !== "privato" && (
                <p className="mt-2 text-xs text-[var(--pd-tag-amber)]">🎁 Hai diritto a fino a {maxMonths} mes{maxMonths === 1 ? "e" : "i"} gratis su un piano standard</p>
            )}
            {(freePeriod?.expires_at || sub.plan_expires_at) && (
                <div className="mt-2 flex items-center gap-2 text-sm text-[var(--pd-muted)]">
                    <Calendar size={13} />
                    {freePeriod?.status === "active" ? "Periodo gratuito fino al" : freePeriod?.status === "grace" ? "Tolleranza fino al" : sub.auto_renew ? "Rinnovo" : "Scadenza"}: {new Date(freePeriod?.status === "grace" ? freePeriod.grace_expires_at : freePeriod?.expires_at || sub.plan_expires_at).toLocaleDateString("it-IT")}
                    {(freePeriod?.days_left ?? sub.days_left) != null && <span className="font-semibold text-[var(--pd-accent)]">· {freePeriod?.days_left ?? sub.days_left}gg</span>}
                </div>
            )}
            {freePeriod?.status === "grace" && (
                <div data-testid="subscription-grace-notice" className="mt-3 rounded-xl border border-[var(--pd-tag-amber)]/30 bg-[var(--pd-tag-amber-bg)] px-3 py-2 text-xs text-[var(--pd-tag-amber)]">Il periodo gratuito è terminato. Completa il pagamento entro il {new Date(freePeriod.grace_expires_at).toLocaleDateString("it-IT")} per mantenere l’accesso.</div>
            )}
            {!sub.auto_renew && hasActivePlan && !sub.is_promo && (
                <div className="mt-3 inline-block rounded-xl border border-[var(--pd-tag-amber)]/30 bg-[var(--pd-tag-amber-bg)] px-3 py-2 text-xs text-[var(--pd-tag-amber)]">Rinnovo disattivato — non ti addebiteremo più nulla.</div>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/prezzi" data-testid="sub-change-plan" className={ghostBtn}>
                    <RefreshCw size={12} /> {paymentRequired ? "Completa pagamento" : hasActivePlan ? "Cambia piano" : "Scegli piano"}
                </Link>
                <Link to="/fatture" data-testid="sub-invoices-link" className={ghostBtn}>Vedi fatture</Link>
                <Link to="/profilo" data-testid="sub-profile-link" className={ghostBtn}>
                    <User size={13} /> Il mio profilo
                </Link>
                {hasActivePlan && sub.auto_renew && !isAddon && (
                    <button data-testid="sub-cancel-btn" onClick={onCancel} disabled={busy} className="inline-flex items-center gap-2 rounded-xl border border-[var(--pd-tag-red)]/40 bg-[var(--pd-surface)] px-4 py-2 text-xs font-medium text-[var(--pd-tag-red)] hover:bg-[var(--pd-tag-red-bg)] transition-colors disabled:opacity-50">
                        <X size={12} /> Recedi
                    </button>
                )}
                {isAgency && (
                    <Link to="/agenzia/calendario" data-testid="sub-calendar-link" className={ghostBtn}>
                        <Calendar size={12} /> Calendario visite
                    </Link>
                )}
                {isAgency && sub?.analytics_pro?.active && (
                    <Link to="/analytics-pro" data-testid="sub-analytics-pro-link" className="inline-flex items-center gap-2 rounded-xl border border-[var(--pd-purple-border)] bg-[var(--pd-purple-bg)] px-4 py-2 text-xs font-medium text-[var(--pd-purple)] hover:opacity-90 transition-opacity">
                        <Sparkles size={12} /> Apri VD Manager
                    </Link>
                )}
            </div>
        </Card>
    );
}

function PromoCard({ sub, busy, onActivatePromo }) {
    const promo = sub.promo || {};
    const perPlan = promo.per_plan_months || {};
    const maxMonths = Object.values(perPlan).reduce((m, v) => Math.max(m, Number(v) || 0), 0);
    return (
        <Card tone="blue" testid="promo-banner">
            <Eyebrow icon={Gift}>Promo lancio · fino a {maxMonths} mes{maxMonths === 1 ? "e" : "i"} grat{maxMonths === 1 ? "is" : "is"}</Eyebrow>
            <p className="mt-3 text-sm leading-relaxed text-[var(--pd-text)]">Attiva ora uno dei piani standard e usalo gratis. Nessuna carta richiesta.</p>
            {(promo.eligible || []).includes("agenzia") && (
                <p data-testid="promo-upgrade-note" className="mt-2 text-xs leading-relaxed text-[var(--pd-muted)]">
                    Valida solo per l&apos;upgrade al piano <strong className="text-[var(--pd-text)]">Agenzia Full (€129/mese)</strong>. Il piano Full non consente l&apos;uso di gestionali esterni: la sincronizzazione automatica feed XML/CSV resta una funzione esclusiva del piano Agenzia Pro (€69).
                </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
                {(promo.eligible || []).map((pid) => {
                    const m = perPlan[pid] || 0;
                    return (
                        <button key={pid} data-testid={`promo-activate-${pid}`} onClick={() => onActivatePromo(pid)} disabled={busy} className={`${solidBtn} capitalize`}>
                            Attiva {pid}{m > 0 ? ` · ${m}m gratis` : ""}
                        </button>
                    );
                })}
            </div>
        </Card>
    );
}

const VdManagerCard = () => (
    <Card tone="purple" testid="analytics-pro-upsell">
        <Eyebrow icon={Sparkles} color="text-[var(--pd-purple)]">VD Manager</Eyebrow>
        <p className="mt-3 text-sm leading-relaxed text-[var(--pd-text)]">CRM completo, Acquisizione Privati, AI Assistant, campagne email/WhatsApp.</p>
        <p className="mt-2 text-sm text-[var(--pd-muted)]">Incluso nel piano Agenzia + VD Manager · €129/mese</p>
        <Link to="/prezzi" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[var(--pd-purple-border)] bg-[var(--pd-surface)] px-4 py-2 text-xs font-medium text-[var(--pd-purple)] hover:bg-[var(--pd-purple-bg)] transition-colors">Scopri →</Link>
    </Card>
);

const NewListingCard = () => (
    <Card testid="header-new-listing-card">
        <Eyebrow icon={Plus}>Nuovo annuncio</Eyebrow>
        <p className="mt-3 text-sm leading-relaxed text-[var(--pd-text)]">Pubblica un nuovo immobile in pochi clic.</p>
        <Link to="/pubblica" data-testid="header-new-listing-link" className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--pd-accent-border)] bg-[var(--pd-accent-bg)] px-4 py-5 text-xs font-semibold text-[var(--pd-accent)] hover:opacity-90 transition-opacity">
            <Plus size={16} /> Aggiungi un nuovo annuncio
        </Link>
    </Card>
);

function NotifyCard({ user, onToggle }) {
    const chatOn = user?.email_notify_chat !== false;
    const listingOn = user?.email_notify_listing_update !== false;
    const Row = ({ on, label, field, testid }) => (
        <button
            type="button"
            data-testid={testid}
            onClick={() => onToggle(field, on)}
            className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${on ? "border-[var(--pd-accent-border)] bg-[var(--pd-accent-bg)]" : "border-[var(--pd-border)] bg-[var(--pd-surface)]"}`}
        >
            {on ? <Bell size={15} className="text-[var(--pd-icon)]" /> : <BellOff size={15} className="text-[var(--pd-muted)]" />}
            <span className="flex-1 text-xs font-medium text-[var(--pd-text)]">{label}</span>
            {on ? <CheckCircle2 size={14} className="text-[var(--pd-accent)]" /> : <span className="text-xs text-[var(--pd-muted)]">Off</span>}
        </button>
    );
    return (
        <Card testid="header-notify-card">
            <Eyebrow icon={Bell}>Notifiche email</Eyebrow>
            <div className="mt-3 space-y-2">
                <Row on={chatOn} label="Notifiche chat" field="email_notify_chat" testid="header-toggle-email-chat" />
                <Row on={listingOn} label="Aggiornamenti annunci" field="email_notify_listing_update" testid="header-toggle-email-listing" />
            </div>
        </Card>
    );
}

export default function DashboardHeader({ sub, user, busy, onCancel, onActivatePromo, onToggle }) {
    if (!sub) {
        return (
            <div className="pro-dash rounded-[22px] border border-[var(--pd-border)] bg-[var(--pd-surface)] p-4">
                <Card><span className="label-eyebrow">Abbonamento</span><p className="mt-3 text-sm text-[var(--pd-muted)]">Caricamento...</p></Card>
            </div>
        );
    }
    const promo = sub.promo || {};
    const perPlan = promo.per_plan_months || {};
    const maxMonths = Object.values(perPlan).reduce((m, v) => Math.max(m, Number(v) || 0), 0);
    const hasAnyPromo = promo.active && maxMonths > 0;

    const pool = [];
    if (hasAnyPromo && !sub.is_promo && !user?.promo_used && user?.role !== "privato") pool.push(<PromoCard key="promo" sub={sub} busy={busy} onActivatePromo={onActivatePromo} />);
    if (user?.role === "agenzia" && !sub?.analytics_pro?.active) pool.push(<VdManagerCard key="vd" />);
    pool.push(<NewListingCard key="new" />);
    pool.push(<NotifyCard key="notify" user={user} onToggle={onToggle} />);
    const [second, third] = pool;

    return (
        <div data-testid="dashboard-header-cards" className="pro-dash grid grid-cols-1 gap-4 rounded-[22px] border border-[var(--pd-border)] bg-[var(--pd-surface)] p-4 shadow-[var(--pd-shadow)] lg:grid-cols-12">
            <div className="lg:col-span-4"><PlanCard sub={sub} user={user} busy={busy} onCancel={onCancel} /></div>
            <div className="lg:col-span-5">{second}</div>
            <div className="lg:col-span-3">{third}</div>
        </div>
    );
}
